import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Plus, PencilSimple, Trash, SpinnerGap, FolderPlus } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = [
  { value: 'new_cpt_opt', label: 'New CPT/OPT', module: 'sales' },
  { value: 'sales_recruiting_direct', label: 'Sales Recruiting Direct', module: 'sales' },
  { value: 'existing_cpt_opt', label: 'Existing CPT/OPT', module: 'sales' },
  { value: 'any_new_h1t', label: 'Any New H1T', module: 'sales' },
  { value: 'existing_h1_gc_usc', label: 'Existing H1/GC/USC', module: 'sales' },
  { value: 'low_end_existing', label: 'Low End - Existing', module: 'sales' },
  { value: 'low_end_new', label: 'Low End - New', module: 'sales' },
  { value: 'existing_bench', label: 'Any Existing Bench', module: 'recruiting' },
  { value: 'new_bench', label: 'Any New Bench', module: 'recruiting' },
  { value: 'core_recruiting', label: 'Core Recruiting', module: 'recruiting' },
  { value: 'c2c', label: 'C2C', module: 'recruiting' },
  { value: 'low_end_existing', label: 'Low End - Existing', module: 'recruiting' },
  { value: 'low_end_new', label: 'Low End - New', module: 'recruiting' },
  { value: 'any_new_h1b', label: 'Any New H1B', module: 'sales' },
  { value: 'internal_transfer', label: 'Internal Transfer', module: 'sales' },
  { value: 'fulltime', label: 'Full-Time', module: 'recruiting' },
];

const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

const EMPTY_FORM = {
  category: '',
  margin_range_min: 0,
  margin_range_max: 999,
  total_placement: 0,
  total_direct: 0,
  sales_recruiter: 0,
  sourcing_recruiter: 0,
  sales_lead: 0,
  sales_manager: 0,
  sourcing_lead: 0,
  sourcing_manager: 0,
  min_months_payroll: 2,
  module: 'sales',
};

const isRecruitingCategory = (categoryValue, categories) => {
  const cat = categories.find((c) => c.value === categoryValue);
  return (cat?.module || 'sales') === 'recruiting';
};

const entryToFormData = (entry, categories) => {
  const recruiting = entry.module === 'recruiting' || isRecruitingCategory(entry.category, categories);
  return {
    ...EMPTY_FORM,
    category: entry.category,
    margin_range_min: entry.margin_range_min ?? 0,
    margin_range_max: entry.margin_range_max ?? 999,
    total_placement: entry.total_placement ?? 0,
    total_direct: entry.total_direct ?? 0,
    sales_recruiter: recruiting
      ? (entry.recruiter ?? entry.sales_recruiter ?? 0)
      : (entry.sales_recruiter ?? 0),
    sales_lead: recruiting
      ? (entry.lead ?? entry.sales_lead ?? 0)
      : (entry.sales_lead ?? 0),
    sales_manager: recruiting
      ? (entry.manager ?? entry.sales_manager ?? 0)
      : (entry.sales_manager ?? 0),
    sourcing_recruiter: entry.sourcing_recruiter ?? 0,
    sourcing_lead: entry.sourcing_lead ?? 0,
    sourcing_manager: entry.sourcing_manager ?? 0,
    min_months_payroll: entry.min_months_payroll ?? 2,
    module: entry.module || (recruiting ? 'recruiting' : 'sales'),
  };
};

const formToPayload = (form, categories) => {
  const recruiting = isRecruitingCategory(form.category, categories);
  const payload = { ...form, module: recruiting ? 'recruiting' : 'sales' };
  if (recruiting) {
    payload.recruiter = form.sales_recruiter;
    payload.lead = form.sales_lead;
    payload.manager = form.sales_manager;
  }
  return payload;
};

const IncentiveMatrixPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [newCat, setNewCat] = useState({ label: '', module: 'sales' });
  const [moduleFilter, setModuleFilter] = useState('sales');

  const allCategories = [
    ...DEFAULT_CATEGORIES,
    ...customCategories.filter(c => !DEFAULT_CATEGORIES.some(d => d.value === c.value)),
  ];

  const filteredCategories = allCategories.filter(c => {
    if (moduleFilter === 'all') return true;
    return (c.module || 'sales') === moduleFilter;
  });

  const [activeCategory, setActiveCategory] = useState('new_cpt_opt');

  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    fetchEntries();
    fetchCategories();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/incentive-matrices`, { withCredentials: true });
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true });
      setCustomCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleOpenModal = (entry = null) => {
    if (entry) {
      setEditEntry(entry);
      setFormData(entryToFormData(entry, allCategories));
    } else {
      setEditEntry(null);
      const recruiting = isRecruitingCategory(activeCategory, allCategories);
      setFormData({
        ...EMPTY_FORM,
        category: activeCategory,
        module: recruiting ? 'recruiting' : 'sales',
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToPayload(formData, allCategories);
      if (editEntry) {
        await axios.put(`${API_URL}/api/admin/incentive-matrices/${editEntry.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/incentive-matrices`, payload, { withCredentials: true });
      }
      setShowModal(false);
      fetchEntries();
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/incentive-matrices/${id}`, { withCredentials: true });
      fetchEntries();
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddCategory = async () => {
    if (!newCat.label.trim()) return;
    setSaving(true);
    try {
      const value = newCat.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      const resp = await axios.post(`${API_URL}/api/admin/categories`, {
        value,
        label: newCat.label.trim(),
        module: newCat.module,
      }, { withCredentials: true });
      setCustomCategories(prev => [...prev, resp.data]);
      setNewCat({ label: '', module: 'sales' });
      setShowCategoryModal(false);
      setActiveCategory(value);
    } catch (error) {
      alert('Failed to add category: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"? This won't delete associated incentive entries.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${cat.id}`, { withCredentials: true });
      setCustomCategories(prev => prev.filter(c => c.id !== cat.id));
      if (activeCategory === cat.value) setActiveCategory('new_cpt_opt');
    } catch (error) {
      alert('Failed to delete: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredEntries = entries.filter((e) => e.category === activeCategory);

  const getMarginLabel = (entry) => {
    if (entry.margin_range_max >= 999) return `> $${entry.margin_range_min}`;
    if (entry.margin_range_min <= 0) return `<= $${entry.margin_range_max}`;
    return `$${entry.margin_range_min} < Rate <= $${entry.margin_range_max}`;
  };

  const isCustomCategory = (val) => customCategories.some(c => c.value === val);
  const isRecruitingForm = isRecruitingCategory(formData.category, allCategories);

  const salesFormFields = [
    { key: 'total_placement', label: 'Total Placement (₹)' },
    { key: 'total_direct', label: 'Total Direct (₹)' },
    { key: 'sales_recruiter', label: 'Sales Recruiter (₹)' },
    { key: 'sourcing_recruiter', label: 'Sourcing Recruiter (₹)' },
    { key: 'sales_lead', label: 'Sales Lead (₹)' },
    { key: 'sales_manager', label: 'Sales Manager (₹)' },
    { key: 'sourcing_lead', label: 'Sourcing Lead (₹)' },
    { key: 'sourcing_manager', label: 'Sourcing Manager (₹)' },
  ];

  const recruitingFormFields = [
    { key: 'total_placement', label: 'Total Placement (₹)' },
    { key: 'sales_recruiter', label: 'Recruiter (₹)' },
    { key: 'sales_lead', label: 'Lead (₹)' },
    { key: 'sales_manager', label: 'Manager (₹)' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">
            Incentive Matrix
          </h1>
          <p className="text-slate-500 mt-1">Configure incentive payouts by category and margin range</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCategoryModal(true)} data-testid="add-category-btn">
            <FolderPlus size={18} className="mr-2" />
            Add Category
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleOpenModal()} data-testid="add-incentive-btn">
            <Plus size={20} className="mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Module Filter */}
      <div className="flex items-center gap-3">
        <Label className="text-sm font-medium whitespace-nowrap">Module:</Label>
        <Select value={moduleFilter} onValueChange={(v) => { setModuleFilter(v); const cats = allCategories.filter(c => v === 'all' || (c.module || 'sales') === v); if (cats.length > 0) setActiveCategory(cats[0].value); }}>
          <SelectTrigger className="w-48" data-testid="module-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            <SelectItem value="sales">Bench Sales</SelectItem>
            <SelectItem value="recruiting">Recruiting</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="flex-wrap h-auto gap-2 bg-slate-100 p-2">
          {filteredCategories.map((cat) => (
            <TabsTrigger
              key={cat.value}
              value={cat.value}
              className="data-[state=active]:bg-white relative group"
              data-testid={`tab-${cat.value}`}
            >
              {cat.label}
              {isCustomCategory(cat.value) && (
                <span
                  className="ml-1.5 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }}
                  title="Delete category"
                >
                  ×
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {filteredCategories.map((cat) => (
          <TabsContent key={cat.value} value={cat.value}>
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-['Outfit']">{cat.label} Incentives</CardTitle>
                  {isCustomCategory(cat.value) && (
                    <span className="text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full">Custom</span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" />
                  </div>
                ) : filteredEntries.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <p>No entries for this category</p>
                    <Button variant="outline" className="mt-4" onClick={() => handleOpenModal()}>
                      Add Entry
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full data-table text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2">Margin Range</th>
                          <th className="px-3 py-2 text-right">Total Placement</th>
                          {cat.module === 'recruiting' ? (
                            <>
                              <th className="px-3 py-2 text-right">Recruiter</th>
                              <th className="px-3 py-2 text-right">Lead</th>
                              <th className="px-3 py-2 text-right">Manager</th>
                            </>
                          ) : (
                            <>
                              <th className="px-3 py-2 text-right">Total Direct</th>
                              <th className="px-3 py-2 text-right">Sales Recruiter</th>
                              <th className="px-3 py-2 text-right">Sourcing Recruiter</th>
                              <th className="px-3 py-2 text-right">Sales Lead</th>
                              <th className="px-3 py-2 text-right">Sales Manager</th>
                              <th className="px-3 py-2 text-right">Sourcing Lead</th>
                              <th className="px-3 py-2 text-right">Sourcing Manager</th>
                            </>
                          )}
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries
                          .sort((a, b) => b.margin_range_min - a.margin_range_min)
                          .map((entry) => (
                            <tr key={entry.id}>
                              <td className="font-medium">{getMarginLabel(entry)}</td>
                              <td className="text-right">{formatCurrency(entry.total_placement)}</td>
                              {cat.module === 'recruiting' ? (
                                <>
                                  <td className="text-right">{formatCurrency(entry.recruiter || entry.sales_recruiter)}</td>
                                  <td className="text-right">{formatCurrency(entry.lead || entry.sales_lead)}</td>
                                  <td className="text-right">{formatCurrency(entry.manager || entry.sales_manager)}</td>
                                </>
                              ) : (
                                <>
                                  <td className="text-right">{formatCurrency(entry.total_direct)}</td>
                                  <td className="text-right">{formatCurrency(entry.sales_recruiter)}</td>
                                  <td className="text-right">{formatCurrency(entry.sourcing_recruiter)}</td>
                                  <td className="text-right">{formatCurrency(entry.sales_lead)}</td>
                                  <td className="text-right">{formatCurrency(entry.sales_manager)}</td>
                                  <td className="text-right">{formatCurrency(entry.sourcing_lead)}</td>
                                  <td className="text-right">{formatCurrency(entry.sourcing_manager)}</td>
                                </>
                              )}
                              <td className="text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button variant="ghost" size="icon" onClick={() => handleOpenModal(entry)}>
                                    <PencilSimple size={14} />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(entry.id)}>
                                    <Trash size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Entry Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editEntry ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => {
                  const recruiting = isRecruitingCategory(v, allCategories);
                  setFormData({ ...formData, category: v, module: recruiting ? 'recruiting' : 'sales' });
                }}>
                  <SelectTrigger data-testid="entry-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Margin Min ($/hr)</Label>
                <Input
                  type="number"
                  value={formData.margin_range_min}
                  onChange={(e) => setFormData({ ...formData, margin_range_min: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Margin Max ($/hr)</Label>
                <Input
                  type="number"
                  value={formData.margin_range_max}
                  onChange={(e) => setFormData({ ...formData, margin_range_max: parseFloat(e.target.value) || 999 })}
                />
              </div>
            </div>

            <div className={`grid gap-4 ${isRecruitingForm ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-4'}`}>
              {(isRecruitingForm ? recruitingFormFields : salesFormFields).map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label className="text-xs">{field.label}</Label>
                  <Input
                    type="number"
                    value={formData[field.key]}
                    onChange={(e) => setFormData({ ...formData, [field.key]: parseFloat(e.target.value) || 0 })}
                    data-testid={`entry-field-${field.key}`}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Min Months Payroll</Label>
              <Input
                type="number"
                value={formData.min_months_payroll}
                onChange={(e) => setFormData({ ...formData, min_months_payroll: parseInt(e.target.value) || 2 })}
                className="w-32"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : null}
                {editEntry ? 'Update' : 'Add'} Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                placeholder="e.g. Direct Hire, International Bench"
                value={newCat.label}
                onChange={(e) => setNewCat({ ...newCat, label: e.target.value })}
                data-testid="new-category-name"
              />
              {newCat.label && (
                <p className="text-xs text-slate-400">
                  Value: <code className="bg-slate-100 px-1 rounded">{newCat.label.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}</code>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Module</Label>
              <Select value={newCat.module} onValueChange={(v) => setNewCat({ ...newCat, module: v })}>
                <SelectTrigger data-testid="new-category-module">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sales">Bench Sales</SelectItem>
                  <SelectItem value="recruiting">Recruiting</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving || !newCat.label.trim()} onClick={handleAddCategory} data-testid="save-category-btn">
              {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : null}
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncentiveMatrixPage;
